import React, { useState } from 'react';
import { Client } from '../types';
import { Users, Search, Plus, Settings, Calendar, Activity, Edit, Shield, Trash2 } from 'lucide-react';
import { AddClientModal } from './AddClientModal';
import { ClientSettingsModal } from './ClientSettingsModal';
import { SystemHealthDashboard } from './SystemHealthDashboard';
import { ConfirmDialog } from './ConfirmDialog';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';

interface MainDashboardProps {
  clients: Client[];
  onNavigateToClient: (clientId: string) => void;
  onUpdateClient: (clientId: string, updates: Partial<Client>) => void;
  onAddClient: (client: Client) => void;
  onDeleteClient: (clientId: string) => void;
}

export const MainDashboard: React.FC<MainDashboardProps> = ({
  clients,
  onNavigateToClient,
  onUpdateClient,
  onAddClient,
  onDeleteClient
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('all');
  const [showAddClientModal, setShowAddClientModal] = useState(false);
  const [clientSettingsModal, setClientSettingsModal] = useState<{ isOpen: boolean; clientId: string | null }>({
    isOpen: false,
    clientId: null
  });
  const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; clientId: string | null; clientName: string }>({
    isOpen: false,
    clientId: null,
    clientName: ''
  });
  const [currentTab, setCurrentTab] = useState<'clients' | 'system'>('clients');
  
  const { hasPermission } = useAuth();
  const { showSuccess, showError, showWarning } = useToast();

  // Enhanced search function with better matching
  const filteredClients = clients.filter(client => {
    // Normalize search term and client data for better matching
    const normalizedSearchTerm = searchTerm.toLowerCase().trim();
    const normalizedClientName = client.name.toLowerCase();
    const normalizedClientEmail = client.email.toLowerCase();
    
    // Check if search term is empty (show all)
    if (!normalizedSearchTerm) {
      const matchesStatus = filterStatus === 'all' || 
                           (filterStatus === 'active' && client.isActive) ||
                           (filterStatus === 'inactive' && !client.isActive);
      return matchesStatus;
    }
    
    // Multiple search criteria
    const matchesName = normalizedClientName.includes(normalizedSearchTerm);
    const matchesEmail = normalizedClientEmail.includes(normalizedSearchTerm);
    const matchesApiId = client.apiId?.toLowerCase().includes(normalizedSearchTerm) || false;
    
    // Also search in banner set names for more comprehensive search
    const matchesBannerSets = client.bannerSets.some(set => 
      set.name.toLowerCase().includes(normalizedSearchTerm)
    );
    
    const matchesSearch = matchesName || matchesEmail || matchesApiId || matchesBannerSets;
    
    const matchesStatus = filterStatus === 'all' || 
                         (filterStatus === 'active' && client.isActive) ||
                         (filterStatus === 'inactive' && !client.isActive);
    
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (isActive: boolean) => {
    return isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800';
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }).format(date);
  };

  const getActiveBannerSets = (client: Client) => {
    return client.bannerSets.filter(set => set.status === 'active').length;
  };

  const getTotalBannerSets = (client: Client) => {
    return client.bannerSets.length;
  };

  const handleOpenClientSettings = (clientId: string) => {
    setClientSettingsModal({ isOpen: true, clientId });
  };

  const handleCloseClientSettings = () => {
    setClientSettingsModal({ isOpen: false, clientId: null });
  };

  const handleUpdateClientSettings = (clientId: string, updates: Partial<Client>) => {
    onUpdateClient(clientId, updates);
  };

  const handleDeleteClient = (clientId: string) => {
    if (!hasPermission('clients', 'delete')) {
      showError('Permission Denied', 'You do not have permission to delete clients');
      return;
    }

    const client = clients.find(c => c.id === clientId);
    if (!client) return;

    // Check if client has banner sets
    if (client.bannerSets.length > 0) {
      showError(
        'Cannot Delete Client', 
        `${client.name} has ${client.bannerSets.length} banner set${client.bannerSets.length !== 1 ? 's' : ''}. Please delete all banner sets first.`
      );
      return;
    }

    setDeleteConfirm({
      isOpen: true,
      clientId,
      clientName: client.name
    });
  };

  const confirmDeleteClient = () => {
    if (deleteConfirm.clientId) {
      onDeleteClient(deleteConfirm.clientId);
      showWarning('Client Deleted', `${deleteConfirm.clientName} has been permanently deleted`);
      setDeleteConfirm({ isOpen: false, clientId: null, clientName: '' });
    }
  };

  const currentClient = clientSettingsModal.clientId 
    ? clients.find(client => client.id === clientSettingsModal.clientId)
    : null;

  // Clear search function
  const clearSearch = () => {
    setSearchTerm('');
  };

  return (
    <div className="h-full overflow-auto">
      <div className="max-w-7xl mx-auto p-6">
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
              <p className="text-gray-600 mt-1">Manage clients, system health, and platform operations</p>
            </div>
            <div className="flex items-center space-x-3">
              {currentTab === 'clients' && (
                <button 
                  onClick={() => setShowAddClientModal(true)}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Plus size={20} />
                  <span>Add Client</span>
                </button>
              )}
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8">
              <button
                onClick={() => setCurrentTab('clients')}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  currentTab === 'clients'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <Users size={16} />
                  <span>Client Management</span>
                </div>
              </button>
              {hasPermission('settings', 'read') && (
                <button
                  onClick={() => setCurrentTab('system')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    currentTab === 'system'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <Shield size={16} />
                    <span>System Health</span>
                  </div>
                </button>
              )}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        {currentTab === 'clients' ? (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-center">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Users className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Total Clients</p>
                    <p className="text-2xl font-bold text-gray-900">{clients.length}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-center">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Activity className="h-6 w-6 text-green-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Active Clients</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {clients.filter(c => c.isActive).length}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-center">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Settings className="h-6 w-6 text-purple-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Banner Sets</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {clients.reduce((total, client) => total + client.bannerSets.length, 0)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-center">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <Calendar className="h-6 w-6 text-orange-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Active Campaigns</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {clients.reduce((total, client) => 
                        total + client.bannerSets.filter(set => set.status === 'active').length, 0
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Enhanced Search and Filter */}
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Search by client name, email, API ID, or banner sets..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {searchTerm && (
                  <button
                    onClick={clearSearch}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    title="Clear search"
                  >
                    ✕
                  </button>
                )}
              </div>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value as any)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Clients</option>
                <option value="active">Active Only</option>
                <option value="inactive">Inactive Only</option>
              </select>
            </div>

            {/* Search Results Info */}
            {searchTerm && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  {filteredClients.length === 0 
                    ? `No clients found matching "${searchTerm}"`
                    : `Found ${filteredClients.length} client${filteredClients.length !== 1 ? 's' : ''} matching "${searchTerm}"`
                  }
                  {filteredClients.length > 0 && (
                    <button
                      onClick={clearSearch}
                      className="ml-2 text-blue-600 hover:text-blue-800 underline"
                    >
                      Clear search
                    </button>
                  )}
                </p>
              </div>
            )}

            {/* Clients Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredClients.map(client => (
                <div
                  key={client.id}
                  className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
                >
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">
                          {/* Highlight search term in client name */}
                          {searchTerm ? (
                            <span dangerouslySetInnerHTML={{
                              __html: client.name.replace(
                                new RegExp(`(${searchTerm})`, 'gi'),
                                '<mark class="bg-yellow-200">$1</mark>'
                              )
                            }} />
                          ) : (
                            client.name
                          )}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {/* Highlight search term in email */}
                          {searchTerm ? (
                            <span dangerouslySetInnerHTML={{
                              __html: client.email.replace(
                                new RegExp(`(${searchTerm})`, 'gi'),
                                '<mark class="bg-yellow-200">$1</mark>'
                              )
                            }} />
                          ) : (
                            client.email
                          )}
                        </p>
                        {client.apiId && (
                          <p className="text-xs text-blue-600 mt-1 font-mono">
                            API: {searchTerm ? (
                              <span dangerouslySetInnerHTML={{
                                __html: client.apiId.replace(
                                  new RegExp(`(${searchTerm})`, 'gi'),
                                  '<mark class="bg-yellow-200">$1</mark>'
                                )
                              }} />
                            ) : (
                              client.apiId
                            )}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(client.isActive)}`}>
                          {client.isActive ? 'Active' : 'Inactive'}
                        </span>
                        <div className="flex items-center space-x-1">
                          <button
                            onClick={() => handleOpenClientSettings(client.id)}
                            className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                            title="Client Settings"
                          >
                            <Edit size={16} />
                          </button>
                          {hasPermission('clients', 'delete') && (
                            <button
                              onClick={() => handleDeleteClient(client.id)}
                              className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                              title="Delete Client"
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Banner Sets:</span>
                        <span className="font-medium text-gray-900">
                          {getActiveBannerSets(client)} / {getTotalBannerSets(client)}
                        </span>
                      </div>

                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Role:</span>
                        <span className="font-medium text-gray-900 capitalize">{client.role}</span>
                      </div>

                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Last Login:</span>
                        <span className="font-medium text-gray-900">{formatDate(client.lastLogin)}</span>
                      </div>

                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Created:</span>
                        <span className="font-medium text-gray-900">{formatDate(client.createdAt)}</span>
                      </div>
                    </div>

                    {/* Banner Sets Preview */}
                    {client.bannerSets.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-gray-100">
                        <p className="text-xs font-medium text-gray-700 mb-2">Recent Banner Sets:</p>
                        <div className="space-y-1">
                          {client.bannerSets.slice(0, 2).map(set => (
                            <div key={set.id} className="flex items-center justify-between text-xs">
                              <span className="text-gray-600 truncate">
                                {/* Highlight search term in banner set names */}
                                {searchTerm ? (
                                  <span dangerouslySetInnerHTML={{
                                    __html: set.name.replace(
                                      new RegExp(`(${searchTerm})`, 'gi'),
                                      '<mark class="bg-yellow-200">$1</mark>'
                                    )
                                  }} />
                                ) : (
                                  set.name
                                )}
                              </span>
                              <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                                set.status === 'active' ? 'bg-green-100 text-green-700' :
                                set.status === 'draft' ? 'bg-yellow-100 text-yellow-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>
                                {set.status}
                              </span>
                            </div>
                          ))}
                          {client.bannerSets.length > 2 && (
                            <p className="text-xs text-gray-500">+{client.bannerSets.length - 2} more</p>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Action Button */}
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <button
                        onClick={() => onNavigateToClient(client.id)}
                        className="w-full px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
                      >
                        Manage Client
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Enhanced Empty State */}
            {filteredClients.length === 0 && (
              <div className="text-center py-12">
                <Users className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">
                  {searchTerm ? 'No clients found' : 'No clients yet'}
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  {searchTerm ? (
                    <>
                      No clients match your search for "{searchTerm}".{' '}
                      <button
                        onClick={clearSearch}
                        className="text-blue-600 hover:text-blue-800 underline"
                      >
                        Clear search
                      </button>{' '}
                      to see all clients.
                    </>
                  ) : (
                    'Get started by adding your first client.'
                  )}
                </p>
              </div>
            )}
          </>
        ) : (
          <SystemHealthDashboard clients={clients} users={[]} />
        )}

        {/* Add Client Modal */}
        <AddClientModal
          isOpen={showAddClientModal}
          onClose={() => setShowAddClientModal(false)}
          onAddClient={onAddClient}
        />

        {/* Client Settings Modal */}
        {currentClient && (
          <ClientSettingsModal
            isOpen={clientSettingsModal.isOpen}
            onClose={handleCloseClientSettings}
            client={currentClient}
            onUpdateClient={(updates) => handleUpdateClientSettings(currentClient.id, updates)}
          />
        )}

        {/* Delete Confirmation Dialog */}
        <ConfirmDialog
          isOpen={deleteConfirm.isOpen}
          title="Delete Client"
          message={`Are you sure you want to permanently delete "${deleteConfirm.clientName}"? This action cannot be undone.`}
          confirmText="Delete Client"
          cancelText="Cancel"
          type="danger"
          onConfirm={confirmDeleteClient}
          onCancel={() => setDeleteConfirm({ isOpen: false, clientId: null, clientName: '' })}
        />
      </div>
    </div>
  );
};